<template>
  <div :class="$style.slider">
    <div
      ref="container"
      :class="$style.sliderContainer"
      class="swiper"
    >
      <div
        class="swiper-wrapper"
        :class="$style.sliderWrapper"
      >

        <div
          v-for="(slide, index) in slides"
          :key="index"
          :class="$style.sliderSlide"
          class="swiper-slide"
          @click="click_banner(slide)"
        >
          <div
            v-if="!$scopedSlots['slide-card']"
          >
            <slot
              name="slide"
              :slide="slide.src"
              :index="index"
              :active-index="activeIndex"
            >
              <img
                :src="slide.src"
                loading="lazy"
                alt=""
              >
            </slot>
          </div>
          <div
            v-else
            :class="$style.cardItem"
          >
            <slot
              name="slide"
              :slide="slide.src"
              :index="index"
              :active-index="activeIndex"
            >
              <img
                :src="slide.src"
                loading="lazy"
                alt=""
              >
            </slot>
            <slot
              name="slide-card"
              :slide="slide"
              :index="index"
              :active-index="activeIndex"
            >
              <div>
                <h4>{{ slide.title }}</h4>
                <p>{{ slide.content }}</p>
              </div>
            </slot>
          </div>
        </div>
      </div>

      <div
        v-if="default_pagination && slides.length > 1"
        class="slider-pagination"
        ref="pagination"
      />
    </div>
    <slot
      name="prev-btn"
    >
      <SliderBtn
        v-if="slides.length > 1"
        class="slider-prev"
        position="left"
      />
    </slot>

    <slot
      name="next-btn"
    >
      <SliderBtn
        v-if="slides.length > 1"
        class="slider-next"
        position="right"
      />
    </slot>
  </div>

</template>

<script lang="ts">
import {
  Component,
  Prop,
  Ref,
  Vue,
  Watch,
} from 'vue-property-decorator';
import Swiper from 'swiper';
import 'swiper/swiper.css';
import { SwiperOption } from '@/types';
import { Navigation, Pagination } from 'swiper/modules';
import SliderBtn from './SliderBtn.vue';

@Component({
  components: {
    SliderBtn,
  },
})
export default class Slider extends Vue {
  @Prop({ type: Array, default: () => ([]) })
  slides!: unknown[];

  @Prop({ type: Object, default: () => ({}) })
  options!: SwiperOption;

  @Ref()
  readonly container!: HTMLDivElement;

  @Ref()
  readonly pagination!: HTMLDivElement;

  @Prop({ type: String, default: '' })
  name!: string;

  /**
   * Дефолтная пагинация (кружки стилизованные по макету)
   */
  @Prop({ type: Boolean, default: false })
  default_pagination!: boolean;

  /**
   * Дефолтная навигация (кнопки стилизованные по макету)
   */
  @Prop({ type: Boolean, default: false })
  default_navigation!: boolean;

  @Prop({ type: String, default: '.slider-next' })
  nextEl!: string;

  @Prop({ type: String, default: '.slider-prev' })
  prevEl!: string;

  /**
   * TODO: убрать инит и придумать как заменить этот костыль
   */
  @Watch('slides')
  onSlidesChange() {
    this.$nextTick(() => {
      if (this.slider) {
        this.destroy_slider();
        this.init_slider();
      }
    });
  }

  @Watch('activeIndex')
  changedIndex(new_val: number, prev_val: number) {
    if (new_val === 0 && prev_val === this.slides.length - 1) {
      setTimeout(() => {
        if (this.slider) {
          this.destroy_slider();
          this.init_slider();
        }
      }, 250);
    } else if (prev_val === 0 && new_val === this.slides.length - 1) {
      setTimeout(() => {
        if (this.slider) {
          this.destroy_slider();
          this.init_slider(new_val);
        }
      }, 250);
    }
  }

  async mounted() {
    await this.init_slider();
  }

  beforeDestroy() {
    this.destroy_slider();
  }

  activeIndex = 0;

  slider: Swiper | null = null;

  get opts_local(): SwiperOption {
    let { pagination, navigation, allowTouchMove } = this.options;

    if (this.default_navigation) {
      navigation = {
        nextEl: this.nextEl,
        prevEl: this.prevEl,
      };
    }

    if (this.slides.length <= 1) {
      allowTouchMove = false;
    } else {
      allowTouchMove = false;
    }

    if (this.default_pagination) {
      pagination = {
        type: 'bullets',
        clickable: true,
        bulletClass: 'slider-bullet',
        bulletActiveClass: 'active',
        el: '.slider-pagination',
      };
    } else {
      pagination = {
        el: null,
      };
    }

    return {
      ...this.options,
      navigation,
      pagination,
      allowTouchMove,
      spaceBetween: this.options?.spaceBetween ? this.options?.spaceBetween : 24,
    };
  }

  async init_slider(slide = 0) {
    this.opts_local.initialSlide = slide || 0;
    // this.slider = new SliderInst(this.container, this.opts_local);
    this.slider = new Swiper(this.$refs.container, {
      ...this.opts_local,
      modules: [
        Navigation, Pagination,
      ],
    });

    if (this.slider) {
      this.activeIndex = this.slider.realIndex;
      this.update_slider();

      this.slider.on('transitionStart', () => {
        this.activeIndex = this.slider ? this.slider.realIndex : 0;
      });
    }
  }

  update_slider() {
    if (this.slider) {
      this.slider.update();
    }
  }

  destroy_slider() {
    if (this.slider) {
      this.slider.destroy();
    }
  }

  click_banner(slide: unknown) {
    this.$emit('slideClick', slide);
  }
}
</script>

<style lang="scss">
.slider-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  z-index: 2;
  bottom: 10px;
  transform: translateX(-50%);
  left: 50%;
  flex-wrap: wrap;
  width: fit-content;
  padding-left: 8px;
  padding-right: 8px;
}

.slider-bullet {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;

  &:last-child {
    margin-right: 0;
  }

  &:before {
    content: '';
    width: 12px;
    height: 12px;
    background-color: #FBD1D5;
    border-radius: 50%;
  }

  &.active {
    &:before {
      background-color: #ED1B2F;
    }
  }
}
</style>
<style module lang="scss">
.slider {
  position: relative;
}

.sliderContainer {
  position: relative;
  overflow: hidden;
}

.prevBtn {
  left: 24px;
}

.nextBtn {
  right: 24px;
}

.cardItem {
  min-height: 592px;
  border-radius: 4px;
  background-color: #fff;
  overflow: hidden;
}

</style>
